import { lazy, Suspense, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Link } from "@/lib/router-compat";
import { useAuthState } from "@/hooks/useAuthState";
import { useLocalizedRoute } from "@/hooks/useLocalizedRoute";
import { setPostAuthIntent } from "@/lib/post-auth-intent";
import { PRICING_PLANS } from "@/lib/pricingMatrix";
import { LANDING_PAGES } from "@/lib/landingPages";
import { SITE_URL } from "@/lib/site-url";
import { ArrowDown, ArrowRight, Check, ChevronLeft, ChevronRight } from "lucide-react";
import heroImg from "@/assets/como-funciona/hero.jpg";
import fase1 from "@/assets/como-funciona/fase1.jpg";
import fase2 from "@/assets/como-funciona/fase2.jpg";
import fase3 from "@/assets/como-funciona/fase3.jpg";
import faseContraste from "@/assets/como-funciona/fase-contraste.jpg";
import fase4 from "@/assets/como-funciona/fase4.jpg";

const LazyAuthModal = lazy(() => import("@/components/LazyAuthModal"));

type Lang = "es" | "en";
type PlanKey = "basico" | "club_pro" | "completo";
const PLAN_ORDER: PlanKey[] = ["basico", "club_pro", "completo"];

/**
 * Página didáctica «Cómo funciona» (/como-funciona · /en/how-it-works).
 * Recorrido vertical: duda → guía de Sara IA → herramienta → profesional humano. No es una página de
 * tarifas: al final hay una sola oferta (Club Pro, 7 días gratis) y flechas
 * para ver los otros planes sin tablas.
 */
const COPY = {
  es: {
    kicker: "Cómo funciona",
    h1: "Nadie te enseñó a llevar tu negocio. Aquí lo aprendes paso a paso, acompañado.",
    sub: "Cuando surge un problema y no sabes a quién acudir, empiezas hablando con Sara IA. Ella te marca los pasos, tú usas las herramientas y, si hace falta, entra un profesional de verdad.",
    cta: "Probar Club Pro 7 días gratis",
    flowKicker: "El camino",
    flowTitle: "De la duda que te quita el sueño a la solución",
    steps: [
      { tag: "La duda", title: "Te surge un problema y estás solo", worry: "«El proveedor me sube un 8 % y no sé si subir precios.»", text: "No buscas en foros ni llamas a tu cuñado. Abres Retail Lemon y se lo cuentas a Sara IA con tus palabras, como se lo contarías a alguien de confianza.", alt: "Comerciante escribiendo una duda en el móvil desde la barra de su cafetería" },
      { tag: "La guía", title: "El Agente te dice qué pasos seguir", worry: "«Vale, ¿y por dónde empiezo?»", text: "Sara IA no te suelta un rollo. Ordena el problema en pasos claros: qué revisar primero, qué números mirar y qué decisión tomar al final.", alt: "Dueño de una ferretería apuntando los pasos a seguir junto al portátil" },
      { tag: "El aprendizaje", title: "Usas la herramienta y aprendes haciendo", worry: "«Ahora sí veo mi margen real.»", text: "Cada paso te lleva a la herramienta justa: calculadora de márgenes, detector de fugas, hoja de ruta de tu sector, subvenciones. Poco a poco aprendes a llevar las riendas tú solo.", alt: "Dueña de una peluquería sonriendo al ver sus números en una tableta" },
      { tag: "El contraste", title: "Vuelves al Agente, insistes y te reafirmas", worry: "«Sara, me sale subir 40 céntimos el café. ¿Cómo se lo digo a mis clientes de siempre?»", text: "Con el resultado en la mano vuelves a Sara IA las veces que haga falta: le enseñas tus números, le preguntas otra vez, pides asesoramiento extra y no das el paso hasta estar seguro.", alt: "Panadero consultando sus números con Sara IA en el móvil desde el mostrador" },
      { tag: "El respaldo humano", title: "Y si se complica, entra un profesional", worry: "«Tengo que pedir una licencia… o ir a juicio con un empleado.»", text: "Para lo delicado no te dejamos con una pantalla: una licencia, un conflicto laboral, un juzgado o un traspaso. Un profesional retail revisa tu caso y te asesora. Y si lo prefieres, desde el primer día.", alt: "Asesor revisando documentos con una comerciante en su tienda" },
    ],
    endTitle: "No vuelves a decidir a solas",
    endText: "Sara IA para el día a día y para repreguntar cuando dudes, herramientas para aprender a hacerlo bien y personas de carne y hueso para cuando de verdad importa.",
    offerKicker: "Empieza hoy",
    offerTitle: "Aunque creas que lo sabes, te acompañamos en cada decisión",
    trial: "7 días gratis",
    after: "Después",
    noCommit: "Sin permanencia. Cancelas cuando quieras.",
    start: "Empezar prueba gratis",
    prev: "Ver plan anterior",
    next: "Ver plan siguiente",
    recommended: "Recomendado",
    seePricing: "Ver todas las tarifas",
    agentLink: "Qué es el Agente Retail IA",
    tools: "Ver herramientas",
    heroAlt: "Comerciante revisando facturas sola en su tienda al cerrar",
  },
  en: {
    kicker: "How it works",
    h1: "Nobody taught you how to run your business. Here you learn it step by step, with backup.",
    sub: "When a problem comes up and you don't know who to turn to, you start by talking to Sara AI. She lays out the steps, you use the tools and, if needed, a real professional steps in.",
    cta: "Try Club Pro free for 7 days",
    flowKicker: "The path",
    flowTitle: "From the worry that keeps you up to the solution",
    steps: [
      { tag: "The worry", title: "A problem comes up and you're alone", worry: "“My supplier is up 8% and I don't know whether to raise prices.”", text: "No forums, no calling your brother-in-law. You open Retail Lemon and tell Sara AI in your own words, like you'd tell someone you trust.", alt: "Shop owner typing a question on a phone at the café counter" },
      { tag: "The guide", title: "The Agent tells you which steps to follow", worry: "“OK, so where do I start?”", text: "Sara AI skips the lecture. She breaks the problem into clear steps: what to check first, which numbers to look at and what to decide at the end.", alt: "Hardware store owner writing down steps next to a laptop" },
      { tag: "Learning", title: "You use the tool and learn by doing", worry: "“Now I can see my real margin.”", text: "Each step takes you to the right tool: margin calculator, leak detector, your sector's roadmap, grants. Little by little you learn to run things yourself.", alt: "Hair salon owner smiling at her numbers on a tablet" },
      { tag: "Double-check", title: "You go back to the Agent, ask again and feel sure", worry: "“Sara, it says I should add 40 cents to the coffee. How do I tell my regulars?”", text: "With the result in hand you go back to Sara AI as many times as you need: show her your numbers, ask again, get extra advice and only act once you're sure.", alt: "Baker checking his numbers with Sara AI on his phone at the counter" },
      { tag: "Human backup", title: "And if it gets tricky, a professional steps in", worry: "“I need a licence… or I'm going to court with an employee.”", text: "For the delicate stuff we don't leave you with a screen: a licence, a staff dispute, a court case or a business transfer. A retail professional reviews your case and advises you. From day one, if you prefer.", alt: "Advisor reviewing documents with a shop owner in her store" },
    ],
    endTitle: "You never decide alone again",
    endText: "Sara AI for everyday questions and second opinions, tools to learn to do it right and real people for when it truly matters.",
    offerKicker: "Start today",
    offerTitle: "Even if you think you've got it, we're with you on every decision",
    trial: "7 days free",
    after: "Then",
    noCommit: "No commitment. Cancel anytime.",
    start: "Start free trial",
    prev: "See previous plan",
    next: "See next plan",
    recommended: "Recommended",
    seePricing: "See all pricing",
    agentLink: "What the Retail AI Agent is",
    tools: "See tools",
    heroAlt: "Shop owner reviewing invoices alone in her store after closing",
  },
} as const;

const STEP_IMAGES = [fase1, fase2, fase3, faseContraste, fase4];

/** Datos estructurados HowTo con los 5 pasos reales de la página. */
export function comoFuncionaHowTo(lang: Lang) {
  const c = COPY[lang];
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: c.h1,
    description: c.sub,
    inLanguage: lang === "es" ? "es-ES" : "en-US",
    image: "https://retaillemon.com/og-como-funciona.jpg",
    step: c.steps.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s.title, text: s.text })),
  };
}

export default function ComoFunciona({ routeLang }: { routeLang: Lang }) {
  useLocalizedRoute();
  const lang = routeLang;
  const c = COPY[lang];
  const es = lang === "es";
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthState();
  const [authOpen, setAuthOpen] = useState(false);
  const [planIdx, setPlanIdx] = useState(1); // Club Pro por defecto
  const planKey = PLAN_ORDER[planIdx]!;
  const plan = PRICING_PLANS.find((p) => p.key === planKey)!;
  const planName = !es && "nameEn" in plan ? plan.nameEn : plan.name;
  const dash = es ? "/dashboard" : "/en/dashboard";
  const copy = LANDING_PAGES.howItWorks;
  const path = es ? copy.pathEs : copy.pathEn;

  const start = (key: PlanKey) => {
    localStorage.setItem("pending_checkout", JSON.stringify({ product_type: "plan", product_key: key, billing_interval: "month", is_paid_intent: true }));
    if (isAuthenticated) { void navigate({ to: dash }); return; }
    setPostAuthIntent({ kind: "path", path: dash });
    setAuthOpen(true);
  };
  const scrollToOffer = () => document.getElementById("empezar")?.scrollIntoView({ behavior: "smooth", block: "start" });

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: c.h1,
      description: c.sub,
      inLanguage: es ? "es-ES" : "en",
      url: `${SITE_URL}${path}`,
      step: c.steps.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s.title, text: s.text })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: es ? "Inicio" : "Home", item: `${SITE_URL}${es ? "/" : "/en"}` },
        { "@type": "ListItem", position: 2, name: c.h1, item: `${SITE_URL}${path}` },
      ],
    },
  ];

  const kicker = (t: string) => <p className="font-body text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">{t}</p>;

  return (
    <>
      <link rel="canonical" href={`${SITE_URL}${path}`} />
      <link rel="alternate" hrefLang="es" href={`${SITE_URL}${copy.pathEs}`} />
      <link rel="alternate" hrefLang="en" href={`${SITE_URL}${copy.pathEn}`} />
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${copy.pathEs}`} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <Header />
      <main className="bg-card pt-20 text-foreground">
        {/* Hero */}
        <section className="container max-w-6xl py-10 sm:py-14">
          <div className="text-center">
            {kicker(c.kicker)}
            <h1 className="mx-auto mt-4 max-w-4xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-balance sm:text-5xl md:text-6xl">{c.h1}</h1>
            <p className="mx-auto mt-5 max-w-2xl font-body text-lg text-muted-foreground text-balance">{c.sub}</p>
            <button type="button" onClick={scrollToOffer} className="mt-8 inline-flex min-h-[48px] items-center gap-2 rounded-full bg-primary px-7 text-sm font-bold text-primary-foreground hover:brightness-95">
              {c.cta}<ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
          <img src={heroImg} alt={c.heroAlt} width={1600} height={1008} fetchPriority="high" className="mt-10 aspect-[16/9] w-full rounded-3xl border border-border object-cover" />
        </section>

        {/* Recorrido vertical */}
        <section className="container max-w-5xl py-12">
          <div className="text-center">
            {kicker(c.flowKicker)}
            <h2 className="mt-2 font-display text-3xl font-bold tracking-tight text-balance sm:text-4xl">{c.flowTitle}</h2>
          </div>
          <ol className="mt-12">
            {c.steps.map((s, i) => (
              <li key={s.title}>
                <article className={`grid items-center gap-6 md:grid-cols-2 md:gap-10 ${i % 2 ? "md:[&>div:first-child]:order-2" : ""}`}>
                  <div className="relative">
                    <img src={STEP_IMAGES[i]} alt={s.alt} width={1200} height={912} loading="lazy" className="aspect-[4/3] w-full rounded-3xl border border-border object-cover" />
                    <span className="absolute -top-4 left-4 flex h-14 w-14 items-center justify-center rounded-full border-4 border-card bg-primary font-display text-2xl font-extrabold text-primary-foreground">{i + 1}</span>
                  </div>
                  <div>
                    <p className="font-body text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">{s.tag}</p>
                    <h3 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">{s.title}</h3>
                    <p className="mt-4 rounded-2xl border-l-4 border-primary bg-secondary/60 p-4 font-display text-lg font-semibold">{s.worry}</p>
                    <p className="mt-4 font-body text-base text-muted-foreground">{s.text}</p>
                  </div>
                </article>
                {i < c.steps.length - 1 && (
                  <div className="flex justify-center py-6" aria-hidden>
                    <div className="flex flex-col items-center">
                      <span className="h-10 w-px bg-border" />
                      <span className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card"><ArrowDown className="h-5 w-5" strokeWidth={1.5} /></span>
                      <span className="h-10 w-px bg-border" />
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ol>
          <div className="mt-14 rounded-3xl bg-foreground p-8 text-center text-background sm:p-12">
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-balance">{c.endTitle}</h2>
            <p className="mx-auto mt-3 max-w-2xl font-body text-base opacity-80">{c.endText}</p>
          </div>
        </section>

        {/* Oferta única con flechas */}
        <section id="empezar" className="scroll-mt-20 border-t border-border bg-secondary/30 py-16">
          <div className="container max-w-xl text-center">
            {kicker(c.offerKicker)}
            <h2 className="mt-2 font-display text-3xl font-bold tracking-tight text-balance">{c.offerTitle}</h2>
            <div className="mt-8 flex items-center gap-2 sm:gap-4">
              <button type="button" aria-label={c.prev} disabled={planIdx === 0} onClick={() => setPlanIdx((i) => Math.max(0, i - 1))} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border bg-card hover:bg-secondary disabled:opacity-30">
                <ChevronLeft className="h-5 w-5" strokeWidth={1.5} />
              </button>
              <div className={`flex-1 rounded-3xl bg-card p-6 text-left sm:p-8 ${planKey === "club_pro" ? "border-2 border-primary" : "border border-border"}`}>
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-display text-2xl font-extrabold">{planName}</h3>
                  {planKey === "club_pro" && <span className="rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground">{c.recommended}</span>}
                </div>
                <p className="mt-1 font-body text-sm text-muted-foreground">{plan.tagline[lang]}</p>
                <p className="mt-5 font-display text-3xl font-extrabold">{c.trial}</p>
                <p className="font-body text-sm text-muted-foreground">{c.after} {plan.price[lang]}{plan.period[lang]}</p>
                <ul className="mt-5 space-y-2">
                  {plan.highlights.filter((h) => !h.es.includes("Lite")).slice(0, 4).map((h) => (
                    <li key={h.es} className="flex items-start gap-2 font-body text-sm"><Check className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />{h[lang]}</li>
                  ))}
                </ul>
                <button type="button" onClick={() => start(planKey)} className="mt-6 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full bg-primary text-sm font-bold text-primary-foreground hover:brightness-95">
                  {c.start}<ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                </button>
                <p className="mt-3 text-center font-body text-xs text-muted-foreground">{c.noCommit}</p>
              </div>
              <button type="button" aria-label={c.next} disabled={planIdx === PLAN_ORDER.length - 1} onClick={() => setPlanIdx((i) => Math.min(PLAN_ORDER.length - 1, i + 1))} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border bg-card hover:bg-secondary disabled:opacity-30">
                <ChevronRight className="h-5 w-5" strokeWidth={1.5} />
              </button>
            </div>
            <div className="mt-4 flex justify-center gap-2" aria-hidden>
              {PLAN_ORDER.map((k, i) => <span key={k} className={`h-2 rounded-full transition-all ${i === planIdx ? "w-6 bg-foreground" : "w-2 bg-border"}`} />)}
            </div>
            <Link to={es ? "/tarifas" : "/en/pricing"} className="mt-6 inline-flex min-h-11 items-center gap-1 text-sm font-semibold underline underline-offset-4">{c.seePricing}</Link>
          </div>
        </section>
      </main>
      <Footer />
      {authOpen && (
        <Suspense fallback={null}>
          <LazyAuthModal open={authOpen} onOpenChange={setAuthOpen} initialMode="signup" intent={{ kind: "path", path: dash }} />
        </Suspense>
      )}
    </>
  );
}
